import { useEffect, useState } from 'react';

export interface TypewriterOptions {
  /** Milliseconds per character typed. */
  typeMs?: number;
  /** Milliseconds per character deleted: faster than typing, as a person does. */
  deleteMs?: number;
  /** How long a finished word stays on screen before it is deleted. */
  holdMs?: number;
  /** The beat on the empty field before the next word starts. */
  pauseMs?: number;
  /**
   * False swaps whole words every `holdMs` instead of typing them, for whoever
   * asked the system for less motion.
   */
  animate?: boolean;
}

interface TypewriterState {
  index: number;
  length: number;
  deleting: boolean;
}

/**
 * Cycles through `words` as somebody typing them: each word is typed a
 * character at a time, held, deleted, and the next one begins. Returns the part
 * of the current word on screen right now.
 *
 * `words` may be a fresh array on every render (a translation lookup is), and
 * may change under it (the language does): the cycle carries on from wherever
 * it is, on the new list.
 */
export function useTypewriter(
  words: string[],
  { typeMs = 70, deleteMs = 35, holdMs = 1800, pauseMs = 300, animate = true }: TypewriterOptions = {},
): string {
  const [state, setState] = useState<TypewriterState>({ index: 0, length: 0, deleting: false });

  const count = words.length;
  const word = count > 0 ? words[state.index % count] : '';

  useEffect(() => {
    if (count === 0) return;

    let delay: number;
    let next: TypewriterState;
    if (!animate) {
      delay = holdMs;
      next = { index: (state.index + 1) % count, length: 0, deleting: false };
    } else if (!state.deleting && state.length < word.length) {
      delay = typeMs;
      next = { ...state, length: state.length + 1 };
    } else if (!state.deleting) {
      delay = holdMs;
      next = { ...state, deleting: true };
    } else if (state.length > 0) {
      delay = deleteMs;
      next = { ...state, length: state.length - 1 };
    } else {
      delay = pauseMs;
      next = { index: (state.index + 1) % count, length: 0, deleting: false };
    }

    const timer = setTimeout(() => setState(next), delay);
    return () => clearTimeout(timer);
  }, [state, word, count, animate, typeMs, deleteMs, holdMs, pauseMs]);

  return animate ? word.slice(0, state.length) : word;
}
